import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Box, Stack, useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import { initializeGA, trackPageView } from "./analytics";
import HomeSidebar from "./components/layout/HomeSidebar";
import Header from "./components/layout/Header";
import TawkMessengerReact from "@tawk.to/tawk-messenger-react";
import InsufficientCreditsDialog from "./dialogs/InsufficientCreditsDialog";
import {
  hasSeenCreditsModal,
  setCredits,
  setHasSeenLowCreditAlert,
  showLowCreditModal,
  hideInsufficientCreditsModal,
} from "./actions/creditActions";
import CreditAlertDialog from "./dialogs/CreditAlertDialog";
import { getUserProfileApi } from "./services/api/userApi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { logout } from "./actions/userActions";
const queryClient = new QueryClient();
initializeGA();

function App() {
  const location = useLocation();
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const tawkMessengerRef = useRef();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const dispatch = useDispatch();
  const {
    credits,
    hasSeenLowCreditAlert,
    isCreditsInitialized,
    insufficientCreditsModalOpen,
  } = useSelector((state) => state.credit);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  useEffect(() => {
    const handleCloseSidebar = () => setIsSidebarOpen(false);
    window.addEventListener("closeHomeSidebar", handleCloseSidebar);
    return () =>
      window.removeEventListener("closeHomeSidebar", handleCloseSidebar);
    return () =>
      window.removeEventListener("closeHomeSidebar", handleCloseSidebar);
  }, []);
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  useEffect(() => {
    if (isCreditsInitialized) {
      if (credits < 10 && !hasSeenLowCreditAlert) {
        dispatch(showLowCreditModal(true));
      } else if (credits >= 10) {
        dispatch(setHasSeenLowCreditAlert(false));
      }
    }
  }, [credits, hasSeenLowCreditAlert, isCreditsInitialized, dispatch]);

  useEffect(() => {
    if (insufficientCreditsModalOpen) {
      console.log("🚨 APP: SHOWING INSUFFICIENT CREDIT TOAST");

      toast.error(
        "You have 0 credits remaining. You cannot use this feature until you get more credits.",
        {
          position: "top-right",
          autoClose: 5000,
        },
      );

      dispatch(hideInsufficientCreditsModal());
    }
  }, [insufficientCreditsModalOpen, dispatch]);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!userInfo?._id) {
          console.warn("User ID not available.");
          return;
        }

        const response = await getUserProfileApi(userInfo._id);

        if (response.status === 200) {
          const data = response.data;
          // data.role = "admin";
          dispatch({
            type: "USER_LOGIN_SUCCESS",
            payload: data,
          });
          dispatch(
            setCredits(
              data.credits,
              data._id,
              data.creditExpiration,
              data.creditRequestCount,
            ),
          );

          dispatch(hasSeenCreditsModal(data.hasSeenModal));
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    const syncLogout = (event) => {
      if (event.key === "logout") {
        dispatch(logout());
      }
    };

    window.addEventListener("storage", syncLogout);

    return () => {
      window.removeEventListener("storage", syncLogout);
    };
  }, [dispatch]);

  return userInfo ? (
    <QueryClientProvider client={queryClient}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        <Header handleSidebarToggle={toggleSidebar} />
        <ToastContainer />
        {userInfo && (
          <div className='App'>
            <TawkMessengerReact
              propertyId='676950dfaf5bfec1dbe0a68c'
              widgetId='1ifpm7q9d'
              ref={tawkMessengerRef}
              onLoad={() => {
                if (tawkMessengerRef.current) {
                  tawkMessengerRef.current.minimize();
                }
                if (window.Tawk_API && userInfo) {
                  // Set user attributes
                  window.Tawk_API.setAttributes(
                    {
                      name: userInfo.name ?? userInfo.email,
                      email: userInfo.email,
                    },
                    (error) => {
                      if (error) {
                        console.log("Error setting attributes:", error);
                      }
                    },
                  );

                  // Set widget to be minimized on startup
                  window.Tawk_API.visitor = {
                    name: userInfo.name ?? userInfo.email,
                    email: userInfo.email,
                  };

                  // Minimize the widget with a delay to ensure it's fully loaded
                  setTimeout(() => {
                    if (window.Tawk_API && window.Tawk_API.minimize) {
                      window.Tawk_API.minimize();
                      console.log("Tawk widget minimized");
                    }
                  }, 1000);
                }
              }}
            />
          </div>
        )}
        <Stack
          sx={{
            marginLeft: isMobileView ? 0 : isSidebarOpen ? "256px" : "72px",
            transition: "margin-left 0.2s ease-in-out",
            flex: 1, // Take remaining height after header
            minHeight: 0,
          }}
          direction='row'
          mt={location.pathname === "/app/home" ? 0 : "64px"}
        >
          <HomeSidebar
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
          {/* This is where child routes will render */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <Outlet />
          </Box>
          {/* <CreditAlertModal />
          <InsufficientCreditsModal /> */}
        </Stack>
      </Box>
    </QueryClientProvider>
  ) : (
    <Navigate to='/' state={{ from: location }} replace />
  );
}

export default App;
